import React, { useEffect, useRef, useState } from 'react';
import { AREAS, CTA_LABEL, NAV } from './content';

// Logo: the existing Primeslink mark + the wordmark set the way the logo sets it
// (letter-spaced, "PRIMES" in the logo's sky blue, "LINK" in its navy).
export function Brand({ onDark = false, size = 'md' }) {
  const mark = size === 'lg' ? 'h-11 w-11' : 'h-9 w-9';
  return (
    <span className="inline-flex items-center gap-3">
      <img src="/brand/mark.png" alt="" className={`${mark} object-contain`} />
      <span className="font-wordmark font-semibold tracking-[0.22em] text-[0.95rem] leading-none">
        <span className="text-sky">PRIMES</span>
        <span className={onDark ? 'text-white' : 'text-navy'}>LINK</span>
      </span>
      <span className="sr-only">Primeslink</span>
    </span>
  );
}

export function Arrow({ className = '' }) {
  return (
    <svg className={`arrow ${className}`} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CtaButton({ small = false, label = CTA_LABEL, className = '' }) {
  return (
    <a href="#book" className={`rb-btn ${small ? 'rb-btn-sm' : ''} ${className}`}>
      {label}
      <Arrow />
    </a>
  );
}

export function Kicker({ children, center = false }) {
  return (
    <div className={center ? 'flex justify-center' : 'flex'}>
      <span className="rb-pill">
        <span className="dot" />
        {children}
      </span>
    </div>
  );
}

// True only inside the build-time prerender (scripts/prerender.js), which saves the page as static HTML.
export const isPrerender = () => typeof window !== 'undefined' && window.__PRERENDER__ === true;

// Adds `is-in` to the element the first time it scrolls into view. Done with a class (not React state or
// Framer Motion inline styles) so the prerendered HTML and the first client render match exactly —
// hydration stays clean, and the saved HTML never has content baked in at opacity:0.
// Hiding before reveal is CSS-only, under html.rb-anim (set by an inline script in public/index.html).
export function useRevealOnView(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el || isPrerender()) return undefined;
    if (!('IntersectionObserver' in window)) {
      el.classList.add('is-in');
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
}

// Fade-and-rise on first scroll into view.
export function Reveal({ children, delay = 0, className = '', as: Tag = 'div' }) {
  const ref = useRef(null);
  useRevealOnView(ref);
  return (
    <Tag ref={ref} className={`rb-reveal ${className}`} style={delay ? { transitionDelay: `${delay}s` } : undefined}>
      {children}
    </Tag>
  );
}

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isHome = window.location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const href = (h) => (isHome ? h : `/${h}`);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-shadow duration-300 bg-white/95 backdrop-blur-md border-b border-ink/10 ${
        scrolled ? 'shadow-[0_8px_24px_-16px_rgba(22,26,58,0.35)]' : ''
      }`}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-6">
        <a href={isHome ? '#top' : '/'} aria-label="Primeslink home" className="rb-tap">
          <Brand />
        </a>

        <nav className="hidden lg:flex items-center gap-8 text-[0.92rem] font-medium text-ink-soft">
          {NAV.map((n) => (
            <a key={n.href} href={href(n.href)} className="hover:text-ink transition-colors">
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={href('#book')} className="rb-btn rb-btn-sm hidden sm:inline-flex">
            {CTA_LABEL}
            <Arrow />
          </a>
          <button
            type="button"
            className="lg:hidden w-11 h-11 grid place-items-center rounded-full border border-ink/15 text-ink"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              {open ? (
                <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              ) : (
                <path d="M3 6h12M3 12h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-ink/10 bg-white px-5 sm:px-8 pb-6 pt-2">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={href(n.href)}
              onClick={() => setOpen(false)}
              className="block py-3 text-lg rb-display border-b border-ink/10"
            >
              {n.label}
            </a>
          ))}
          <a href={href('#book')} onClick={() => setOpen(false)} className="rb-btn mt-5 w-full justify-center">
            {CTA_LABEL}
            <Arrow />
          </a>
        </div>
      )}
    </header>
  );
}

// The scorecard card — used for the hero sample and the self-check result.
export function Scorecard({ title, subtitle, scores, leak, footnote, compact = false }) {
  const ref = useRef(null);
  useRevealOnView(ref);
  const lowest = Math.min(...scores);
  return (
    <div ref={ref} className="rb-score rb-card shadow-[0_30px_60px_-30px_rgba(22,26,58,0.35)] overflow-hidden">
      <div className="px-6 pt-5 pb-4 border-b border-ink/10 flex items-start justify-between gap-4">
        <div>
          <div className="rb-label !text-brand">{title}</div>
          <div className="mt-1.5 font-semibold text-ink text-[0.98rem]">{subtitle}</div>
        </div>
        <img src="/brand/mark.png" alt="" className="h-7 w-7 opacity-80" />
      </div>
      <ul className={`px-6 ${compact ? 'py-3' : 'py-4'} space-y-3`}>
        {AREAS.map((area, i) => {
          const s = scores[i];
          const low = s === lowest;
          return (
            <li key={area} className="grid grid-cols-[1.3rem_1fr_auto] items-center gap-x-3 gap-y-1.5">
              <span className="font-mono text-[0.875rem] text-ink-mute">{i + 1}</span>
              <span className={`text-[0.92rem] ${low ? 'font-semibold text-ink' : 'text-ink-soft'}`}>{area}</span>
              <span className={`font-mono text-[0.875rem] ${low ? 'text-[#c2410c]' : 'text-ink-mute'}`}>
                {s} / 10
              </span>
              <span />
              <div className={`rb-bar col-span-2 ${low ? 'low' : ''}`}>
                <span style={{ width: `${s * 10}%`, transitionDelay: `${0.3 + i * 0.08}s` }} />
              </div>
            </li>
          );
        })}
      </ul>
      {leak && (
        <div className="mx-4 mb-4 rounded-xl bg-paper px-4 py-3.5 border border-ink/10 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-ink text-[0.92rem]">{leak.title}</div>
            <div className="text-[0.875rem] text-ink-mute mt-0.5">{leak.detail}</div>
          </div>
          {leak.est && <div className="font-mono text-[0.875rem] text-brand whitespace-nowrap">{leak.est}</div>}
        </div>
      )}
      {footnote && <div className="px-6 pb-4 -mt-1 text-[0.875rem] leading-snug text-ink-mute">{footnote}</div>}
    </div>
  );
}
